import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { env } from '@/env'
import { decodeServerMessage, decompressServerMessage, extractError } from '@/lib/proto/codec'
import { serviceUtil } from '@/lib/utils/service-util'
import { ApiRequest } from '@/types/generated/public_api'

const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: env.NEXT_PUBLIC_API_URL,
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    responseType: 'arraybuffer',
  })

  client.interceptors.request.use(async (config) => {
    const { method, data = {} } = config

    // Skip encoding if data is already binary (e.g., retry request)
    const isAlreadyBinary =
      data instanceof ArrayBuffer ||
      Buffer.isBuffer(data) ||
      data instanceof Uint8Array ||
      config._retry

    // Check token expiration and refresh if needed
    const validToken = await serviceUtil.ensureTokenValid()

    if (validToken && config.headers) {
      config.headers.Authorization = `Bearer ${validToken}`
    }

    if (method?.toLowerCase() !== 'get' && !isAlreadyBinary) {
      // Every call posts to the same endpoint; keep which operation it was for error logs.
      config._op = Object.keys(data)[0]
      const request = ApiRequest.create(data)
      const binaryData = ApiRequest.encode(request).finish()
      config.data = binaryData
      config.headers['Content-Type'] = 'application/octet-stream'
    }
    config.transformRequest = [(data) => data]

    return config
  })

  client.interceptors.response.use(
    async (response) => {
      if (
        (response.config.responseType === 'arraybuffer' && Buffer.isBuffer(response.data)) ||
        response.data instanceof ArrayBuffer
      ) {
        try {
          const decodeKey = response.config.headers?.['X-Decode-Key'] as string | undefined
          const serverMsg = decodeServerMessage(response.data)
          const instrument = await decompressServerMessage(serverMsg)

          if (instrument) {
            response.data = { instrumentList: instrument }
            return response
          }

          // if (env.NEXT_PUBLIC_ENV !== 'prod') {
          //   console.log('[client.ts Line:60]', serverMsg)
          // }
          if (decodeKey === 'datapayload') {
            response.data = serverMsg.data
          } else {
            response.data = serverMsg
          }
        } catch (e) {
          console.log(e)
          return Promise.reject({
            status: 500,
            message: 'Response decoding failed',
            data: response.data,
          })
        }
      }
      return response
    },
    async (error: unknown) => {
      // Not every rejection reaching here came off the wire: the request
      // interceptor's `ensureTokenValid()` rejects with its own
      // `{ status, message }`, which must survive rather than be relabelled.
      if (!axios.isAxiosError(error)) {
        if (!(error as { transient?: boolean })?.transient) {
          console.error('[client] non-axios request/response failure', error)
        }
        return Promise.reject(
          error && typeof error === 'object' && 'status' in error
            ? error
            : { status: 500, message: 'Request failed' }
        )
      }

      // A transport failure (offline, CORS, aborted) has no response body at
      // all, and a body that is not a ServerMessage decodes to garbage or
      // throws — neither is worth losing the underlying axios error over.
      if (error.response?.data) {
        try {
          const serverMsg = decodeServerMessage(error.response.data)
          error.response.data = extractError(serverMsg)
        } catch (decodeError) {
          console.error('[client] could not decode the error response', decodeError)
        }
      }

      const axiosError = error as AxiosError<any>
      const originalRequest = axiosError.config

      // Use serviceUtil to handle the error
      try {
        return await serviceUtil.handleAxiosResponseErrorInterceptor(error, client, originalRequest)
      } catch (handledError) {
        return Promise.reject(handledError)
      }
    }
  )

  return client
}

export const orderXClient = createApiClient()
